/**
 * 使い勝手フィードバック⑥ (D-U28): 申請フォームの入力値バリデーション (クライアント側ミラー)。
 *
 * ⚠️ サーバー (apps/api/src/lib/request-instances/form.ts の validateOne / evaluateErrorConditions、
 * apps/api/src/lib/request-types/field-validation.ts の checkFieldConstraints) が唯一の正である。
 * ここは blur/提出時に即座にフィードバックを返す表示専用の実装で、評価が失敗 (構造不明・未対応の型等)
 * した場合は「エラー無し」と断定せず、提出をブロックしない (サーバーの結果を正とする、§7-4 の不変条件)。
 *
 * サーバーと同じメッセージ文言 (D-U25 の解決済み label、I6-04) にすること — でないと「入力中に見た
 * 警告」と「提出時にサーバーから返ってきた警告」の文言が食い違い、利用者が混乱する (§8-134 と同種の事故)。
 */
import { evaluateAstSafe, isRealDate, type AstValue } from './request-form-ast';

// ---------------------------------------------------------------------------
// 型 (RequestFormFields.tsx の FieldDefinition/FieldCondition と構造的に互換であればよい。
// 循環 import を避けるため import type ではなくここで独立に定義する)
// ---------------------------------------------------------------------------
export type ValidationConstraintsInput = {
  min_length?: number; max_length?: number;
  min?: number; max?: number; decimal_places?: number;
  min_date?: string; max_date?: string;
  min_items?: number; max_items?: number;
  format?: string;
};
export type FieldConstraintsInput = {
  field_definition_id: string;
  /** D-U25 の解決済み表示名 (常に非 null)。サーバーの検証メッセージもこれを使う (I6-04)。 */
  label: string;
  field_type: string;
  field_options: Array<{ value: string; label: string }> | null;
  validation_json?: ValidationConstraintsInput | null;
};
export type FieldConditionWithErrorInput = {
  target_field_definition_id: string;
  effect: string;
  expression_ast: unknown;
  error_message?: string | null;
};
export type FieldStateInput = { visible: boolean; required: boolean };

// ⚠️ apps/api/src/lib/request-types/field-validation.ts の VALIDATION_FORMATS と同じキー・
// 正規表現にすること (プラットフォーム提供の固定5種、正規表現をテナントに自由入力させない D-U28層2)。
export const VALIDATION_FORMATS: Record<string, { label: string; example: string; pattern: RegExp }> = {
  phone_jp: { label: '電話番号 (日本)', example: '03-1234-5678', pattern: /^0(\d{1,4}-\d{1,4}-\d{4}|\d{9,10})$/ },
  postal_jp: { label: '郵便番号 (日本)', example: '123-4567', pattern: /^\d{3}-?\d{4}$/ },
  email: { label: 'メールアドレス', example: 'user@example.com', pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
  katakana: { label: 'カタカナ', example: 'ヤマダ タロウ', pattern: /^[ァ-ヶー\s]+$/ },
  alphanumeric: { label: '半角英数字', example: 'abc123', pattern: /^[A-Za-z0-9]+$/ },
};

// ⚠️ apps/api/src/lib/request-types/field-validation.ts の PLATFORM_MAX_* (= apps/api/src/lib/
// request-instances/form.ts の base 型検証ハード上限) と同じ数値にすること。別パッケージのため
// import できず3箇所目の定義になる (§8-93 型のドリフトリスク、design-and-code レビュー指摘)。
const MAX_TEXT_LEN = 500;
const MAX_TEXTAREA_LEN = 5000;
const MAX_MULTI_SELECT = 100;
const MAX_MULTI_DATE = 366;

const RELATIVE_DATE_RE = /^today(?:([+-])(\d{1,4})d)?$/;

/** ⚠️ §0-2: 日時を扱う関数は asOf を引数で受け取る純関数にする。 */
const resolveRelativeDate = (spec: string | undefined, asOf: string): string | null => {
  if (!spec) return null;
  const m = RELATIVE_DATE_RE.exec(spec);
  if (m) {
    if (!m[1]) return asOf;
    const sign = m[1] === '-' ? -1 : 1;
    const days = sign * Number(m[2]);
    const base = new Date(`${asOf}T00:00:00Z`);
    if (Number.isNaN(base.getTime())) return null;
    base.setUTCDate(base.getUTCDate() + days);
    return base.toISOString().slice(0, 10);
  }
  return isRealDate(spec) ? spec : null;
};

const isEmptyValue = (v: unknown): boolean =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/** 層1 (上限・範囲) + 層2 (書式)。base 型検証を通過した値にのみ適用する。 */
const checkConstraints = (f: FieldConstraintsInput, value: unknown, asOf: string): string | null => {
  const c = f.validation_json;
  if (!c) return null;
  const label = f.label;
  switch (f.field_type) {
    case 'text':
    case 'textarea': {
      const v = value as string;
      if (typeof c.min_length === 'number' && v.length < c.min_length) return `「${label}」は${c.min_length}文字以上で入力してください`;
      if (typeof c.max_length === 'number' && v.length > c.max_length) return `「${label}」は${c.max_length}文字以内で入力してください`;
      if (typeof c.format === 'string') {
        const fmt = VALIDATION_FORMATS[c.format];
        if (fmt && !fmt.pattern.test(v)) return `「${label}」は${fmt.label}の形式で入力してください (例: ${fmt.example})`;
      }
      return null;
    }
    case 'number': {
      const v = value as number;
      if (typeof c.decimal_places === 'number') {
        const scale = 10 ** c.decimal_places;
        if (Math.abs(Math.round(v * scale) - v * scale) > 1e-6) return `「${label}」は小数第${c.decimal_places}位までで入力してください`;
      }
      if (typeof c.min === 'number' && v < c.min) return `「${label}」は${c.min}以上で入力してください`;
      if (typeof c.max === 'number' && v > c.max) return `「${label}」は${c.max}以下で入力してください`;
      return null;
    }
    case 'date': {
      const v = value as string;
      const min = resolveRelativeDate(c.min_date, asOf);
      const max = resolveRelativeDate(c.max_date, asOf);
      if (min && v < min) return `「${label}」は${min}以降の日付にしてください`;
      if (max && v > max) return `「${label}」は${max}以前の日付にしてください`;
      return null;
    }
    case 'date_range': {
      const { start, end } = value as { start: string; end: string };
      const min = resolveRelativeDate(c.min_date, asOf);
      const max = resolveRelativeDate(c.max_date, asOf);
      if (min && (start < min || end < min)) return `「${label}」は${min}以降の日付にしてください`;
      if (max && (start > max || end > max)) return `「${label}」は${max}以前の日付にしてください`;
      return null;
    }
    case 'multi_date': {
      const v = value as string[];
      if (typeof c.min_items === 'number' && v.length < c.min_items) return `「${label}」は${c.min_items}件以上指定してください`;
      if (typeof c.max_items === 'number' && v.length > c.max_items) return `「${label}」は${c.max_items}件以内で指定してください`;
      const min = resolveRelativeDate(c.min_date, asOf);
      const max = resolveRelativeDate(c.max_date, asOf);
      if (min && v.some((d) => d < min)) return `「${label}」は${min}以降の日付にしてください`;
      if (max && v.some((d) => d > max)) return `「${label}」は${max}以前の日付にしてください`;
      return null;
    }
    case 'multi_select': {
      const v = value as string[];
      if (typeof c.min_items === 'number' && v.length < c.min_items) return `「${label}」は${c.min_items}件以上選択してください`;
      if (typeof c.max_items === 'number' && v.length > c.max_items) return `「${label}」は${c.max_items}件以内で選択してください`;
      return null;
    }
    default:
      return null;
  }
};

/** base 型 (サーバーの validateBaseType のミラー) → 層1/層2。
 *  ⚠️ 未対応の型 (default 節) は「判定不能として提出を止めない」= null を返す (§7-4 の不変条件)。 */
const checkFieldValue = (f: FieldConstraintsInput, value: unknown, asOf: string): string | null => {
  const label = f.label;
  const options = (f.field_options ?? []).map((o) => o.value);
  switch (f.field_type) {
    case 'text':
    case 'textarea': {
      if (typeof value !== 'string') return `「${label}」は文字列で入力してください`;
      const max = f.field_type === 'text' ? MAX_TEXT_LEN : MAX_TEXTAREA_LEN;
      if (value.length > max) return `「${label}」は${max}文字以内で入力してください`;
      break;
    }
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) return `「${label}」は数値で入力してください`;
      break;
    case 'boolean':
      if (typeof value !== 'boolean') return `「${label}」は真偽値で入力してください`;
      break;
    case 'date':
      if (typeof value !== 'string' || !isRealDate(value)) return `「${label}」は実在する日付 (YYYY-MM-DD) で入力してください`;
      break;
    case 'date_range': {
      if (value === null || typeof value !== 'object' || Array.isArray(value)) return `「${label}」は開始日と終了日を指定してください`;
      const { start, end } = value as { start?: unknown; end?: unknown };
      if (typeof start !== 'string' || !isRealDate(start)) return `「${label}」の開始日が不正です`;
      if (typeof end !== 'string' || !isRealDate(end)) return `「${label}」の終了日が不正です`;
      if (end < start) return `「${label}」の終了日は開始日以降にしてください`;
      break;
    }
    case 'multi_date': {
      if (!Array.isArray(value)) return `「${label}」は日付の配列で指定してください`;
      if (value.length > MAX_MULTI_DATE) return `「${label}」に指定できる日付は${MAX_MULTI_DATE}件までです`;
      const seen = new Set<string>();
      for (const v of value) {
        if (typeof v !== 'string' || !isRealDate(v)) return `「${label}」に実在しない日付が含まれています`;
        if (seen.has(v)) return `「${label}」に同じ日付 (${v}) が重複しています`;
        seen.add(v);
      }
      break;
    }
    case 'time':
      if (typeof value !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return `「${label}」は時刻 (HH:MM) で入力してください`;
      break;
    case 'select':
      if (typeof value !== 'string' || !options.includes(value)) return `「${label}」に選択できない値が指定されています`;
      break;
    case 'multi_select': {
      if (!Array.isArray(value)) return `「${label}」は選択肢の配列で指定してください`;
      if (value.length > MAX_MULTI_SELECT) return `「${label}」の選択数が多すぎます`;
      for (const v of value) {
        if (typeof v !== 'string' || !options.includes(v)) return `「${label}」に選択できない値が含まれています`;
      }
      break;
    }
    case 'file':
      // 使い勝手フィードバック増分⑧ (D-U26): 添付は values に値を持たない (別テーブル・別API
      // で管理する)。ここに到達するのは想定外の値が直接送られてきた場合のみ。
      return `「${label}」に値を直接指定することはできません`;
    default:
      return null; // 未知の型: 提出はブロックせずサーバーの判定に委ねる
  }
  return checkConstraints(f, value, asOf);
};

const toAstValueLocal = (v: unknown): AstValue => {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
  return null;
};

/**
 * フォーム全体の入力値バリデーション。visible な項目のみを対象にし、項目1件につき最初に
 * 見つかったエラー1件だけを報告する (§7-5「項目ごとに1件目のみ」)。
 * 優先順位: 必須未入力 → base型/層1/層2 → 層3 (項目間の相関、error 条件)。
 */
export const validateFormFields = (
  fields: FieldConstraintsInput[],
  conditions: FieldConditionWithErrorInput[],
  states: Map<string, FieldStateInput>,
  values: Record<string, unknown>,
  asOf: string,
): Map<string, string> => {
  const errors = new Map<string, string>();
  for (const f of fields) {
    const state = states.get(f.field_definition_id);
    if (!state || !state.visible) continue;
    // 使い勝手フィードバック増分⑧ (D-U26): file 型は添付の有無 (別テーブル) で必須判定するため、
    // ここでの「必須なのに値が空」判定の対象から外す (form.ts の validateAndNormalize と同じ)。
    // 添付が無い場合のエラーはサーバー (POST /:id/submit) が返す。
    // ⚠️ ただし非空の値が直接送られてきた場合はサーバーの validateOne と同じく明示的に拒否する
    // (単体テストで発見: 従来は field_type==='file' で無条件に continue しており、
    // checkFieldValue の 'file' ケースが呼び出し元から一切到達しない死んだ分岐になっていた)。
    if (f.field_type === 'file') {
      const fileValue = values[f.field_definition_id];
      if (!isEmptyValue(fileValue)) {
        const fileErr = checkFieldValue(f, fileValue, asOf);
        if (fileErr) errors.set(f.field_definition_id, fileErr);
      }
      continue;
    }
    const value = values[f.field_definition_id];
    if (isEmptyValue(value)) {
      if (state.required) errors.set(f.field_definition_id, `「${f.label}」は必須です`);
      continue;
    }
    const err = checkFieldValue(f, value, asOf);
    if (err) errors.set(f.field_definition_id, err);
  }

  // 層3: 項目間の相関 (effect='error')。sort_order は呼び出し元の並びをそのまま尊重する
  // (サーバーは sort_order 昇順で最初の一致を報告するが、ここでは配列順を利用する)。
  const valueMap = new Map<string, AstValue>(fields.map((f) => [f.field_definition_id, toAstValueLocal(values[f.field_definition_id])]));
  for (const cond of conditions) {
    if (cond.effect !== 'error') continue;
    if (errors.has(cond.target_field_definition_id)) continue; // 項目ごとに1件目のみ (§7-5)
    const state = states.get(cond.target_field_definition_id);
    if (!state || !state.visible) continue;
    // ⚠️ evaluateAstSafe は失敗を握り潰し null を返す (= "not matched" と同義に扱う)。
    // §7-4 の不変条件どおり「評価不能を valid と断定しない」が、ここでは「エラーを出さない」だけで
    // 提出のブロックには使わない (最終判定はサーバー) ため、これで問題ない。
    if (evaluateAstSafe(cond.expression_ast, valueMap) === true) {
      errors.set(cond.target_field_definition_id, cond.error_message ?? '入力内容に誤りがあります');
    }
  }
  return errors;
};
